import { DateRange } from '@automata/rexports/x-date-pickers-pro';
import dayjs, { ConfigType, Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import timeZone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(duration);
dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);
dayjs.extend(timeZone);
dayjs.extend(utc);

export type HoursMinutesSeconds = {
  hours: number;
  minutes: number;
  seconds: number;
};

export type HoursMinutesSecondsUnix = HoursMinutesSeconds & {
  unix: number;
};

export const formatTime = (time: number): HoursMinutesSecondsUnix => {
  const hours = Math.floor((time / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((time / (1000 * 60)) % 60);
  const seconds = Math.floor((time / 1000) % 60);

  return { hours, minutes, seconds, unix: time };
};

/**
 *
 * @returns The local timezone code.
 * @example
 * getTimeZoneCode();
 * // Returns 'BST' (depending on the timezone)
 */
export const getTimeZoneCode = (): string => {
  const { locale } = Intl.DateTimeFormat().resolvedOptions();

  const options: Intl.DateTimeFormatOptions = {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timeZoneName: 'short',
  };

  const formatter = new Intl.DateTimeFormat(locale, options);
  const parts = formatter.formatToParts();
  const timeZonePart = parts.find((part) => part.type === 'timeZoneName');
  return timeZonePart ? timeZonePart.value : '';
};

/**
 * Formats a date value to a string.
 * @param value The date value to format.
 * @param compact Whether to format the date in a compact format.
 * @returns The formatted date string.
 * @example
 * dateValueFormatter('2022-01-01T00:00:00.000Z');
 * // Returns 'Sat, 1st Jan, 2022 (00:00)'
 * @example
 * dateValueFormatter('2022-01-01T00:00:00.000Z', true);
 * // Returns '01/01/22 - 00:00:00'
 */
export const dateValueFormatter = (
  value?: ConfigType,
  compact: boolean = false
): string => {
  if (!value || !dayjs(value).isValid()) return '';
  const convertedValue =
    typeof value === 'number' ? dayjs.unix(value) : dayjs(value);

  const tzc = getTimeZoneCode();

  if (compact) return dayjs(convertedValue).format('DD-MMM-YYYY HH:mm:ss');
  return `${dayjs(convertedValue).format('DD-MMM-YYYY HH:mm:ss')} ${tzc}`;
};

/**
 *
 * @param time a unix timestamp
 * @returns the time lapsed since the given time
 * @example
 * getTimeLapsed(1640995200000);
 * // Returns 'a few seconds ago'
 */
export const getTimeLapsedFromNow = (time?: ConfigType): string => {
  if (!time || !dayjs(time).isValid()) return '';
  if (typeof time === 'number') return dayjs.unix(time).fromNow();
  return dayjs(time).fromNow();
};

/**
 * Formats a time value to a string.
 * @param value The time value to format.
 * @param compact Whether to format the time in a compact format.
 * @returns The formatted time string.
 * @example
 * timeValueFormatter('2022-01-01T05:10:00.000Z');
 * // Returns '05:10:00'
 * @example
 * timeValueFormatter('2022-01-01T05:10:00.000Z', true);
 * // Returns '05:10'
 */
export const timeValueFormatter = (
  value?: ConfigType,
  compact: boolean = false
) => {
  if (!value || !dayjs(value).isValid()) return '';
  if (compact) return dayjs(value).format('HH:mm');
  return dayjs(value).format('HH:mm:ss');
};

/**
 * Formats a duration value to a string.
 * @param amount The duration to format.
 * @param compact Whether to format the time in a compact format.
 * @param unit The unit of the time value (defaults to 'minutes').
 * @returns The formatted time string.
 * @example
 * durationValueFormatter(125);
 * // Returns '00:02:05'
 * @example
 * durationValueFormatter(125, false, 'minutes');
 * // Returns '02:05:00'
 * @example
 * durationValueFormatter(125, true);
 * // Returns '00:02'
 */
export const durationValueFormatter = (
  amount?: number,
  compact: boolean = false,
  unit: 'seconds' | 'minutes' | 'hours' = 'seconds'
) => {
  if (!amount || !dayjs(amount).isValid()) return '';
  if (compact) return dayjs.duration(amount, unit).format('HH:mm');
  return dayjs.duration(amount, unit).format('HH:mm:ss');
};

/**
 * Adds a delay to the given time value.
 * @param time The ISO string representing the time value to add the delay to.
 * @param delay The delay to add to the time value.
 * @param unit The unit of the delay value (defaults to 'seconds').
 * @returns The ISO string representing the new time value with the delay added.
 * @example
 * addDelayToTime('2022-01-01T00:00:00.000Z', 10);
 * // Returns '2022-01-01T00:00:10.000Z'
 */
export const addDelayToTime = (
  time: ConfigType,
  delay: number | undefined,
  unit: 'seconds' | 'minutes' | 'hours' = 'seconds'
): string => {
  if (!dayjs(time).isValid()) return '';
  if (!delay) return dayjs(time).toISOString();
  return dayjs(time).add(delay, unit).toISOString();
};

/**
 * Gets the number of seconds elapsed between two time values expressed as absolute value.
 * @param startTime The ISO string representing the start time for the computation.
 * @param endTime The ISO string representing the end time for the computation.
 * @returns The elapsed time between the two time values.
 * @example
 * getElapsedTime('2022-01-01T00:00:00.000Z', '2022-01-01T00:00:10.000Z');
 * // Returns 10
 * @example
 * getElapsedTime('2022-01-01T00:00:10.000Z', '2022-01-01T00:00:00.000Z');
 * // Returns 10
 */
export const getElapsedTime = (
  startTime: ConfigType,
  endTime: ConfigType
): number => {
  const start = dayjs(startTime);
  const end = dayjs(endTime);
  if (!start.isValid() || !end.isValid()) return 0;
  if (end.isBefore(start)) return 0;
  const duration = dayjs.duration(start.diff(end)).asSeconds();
  return Math.abs(duration);
};

/**
 * Converts a date value to an epoch timestamp (number of **milliseconds** since the Unix Epoch).
 * @param date The date value to convert.
 * @returns The epoch timestamp.
 * @example
 * toEpochTimeStamp('2022-01-01T00:00:00.000Z');
 * // Returns 1640995200000
 */
export const toEpochTimeStamp = (date: ConfigType): number => {
  return dayjs(date).valueOf();
};

/**
 * Converts a date value to a Date object.
 * @param date The date value to convert.
 * @returns The Date object.
 */
export const toDateObject = (date: ConfigType): Date => {
  if (!dayjs(date).isValid()) return new Date();
  return dayjs(date).toDate();
};

/**
 * Subtracts the given number of days from the given date value.
 * @param days
 * @param date If none is provided, the current date is used.
 * @returns The ISO string representing the new date value.
 * @example
 * subtractDays(1, '2022-01-02T00:00:00.000Z');
 * // '2022-01-01T00:00:00.000Z'
 */
export const subtractDays = (
  days: number,
  date: ConfigType = new Date()
): string => {
  return dayjs(date).subtract(days, 'days').toISOString();
};

/**
 * Turns the given number of seconds into an object with hours, minutes, seconds, and the total number of seconds.
 *
 * **Known Issue**: The function does not handle durations exceeding 30 days correctly.
 * @param totalSeconds The number of seconds to convert.
 * @returns The object with the hours, minutes, seconds, and total number of seconds.
 * @example
 * secondsToHoursMinutesSecondsUnix(3723);
 * // { hours: 1, minutes: 2, seconds: 3, unix: 3723 }
 */
export const secondsToHoursMinutesSecondsUnix = (
  totalSeconds: number
): HoursMinutesSecondsUnix => {
  const duration = dayjs.duration(totalSeconds, 'seconds');

  return {
    hours: duration.days() * 24 + duration.hours(),
    minutes: duration.minutes(),
    seconds: duration.seconds(),
    unix: totalSeconds,
  };
};

/**
 * Formats the given time object into a string representation of hours, minutes, and seconds.
 *
 * @param time - The time object to format as HoursMinutesSecondsUnix.
 * @returns A string representation of the time in the format "HH:MM:SS".
 * @example
 * formatHoursMinutesSecondsUnixToString({ hours: 1, minutes: 2, seconds: 3, unix: 3723});
 * // Returns "01:02:03"
 */
export const formatHoursMinutesSecondsUnixToString = (
  time: HoursMinutesSecondsUnix
): string => {
  return [time.hours, time.minutes, time.seconds]
    .map(getPaddedClampedNumber)
    .join(':');
};

/**
 * Formats the given number of seconds into hh:mm:ss.
 * If the time value is undefined or 0 and returnPlaceholder is true, it returns '--:--:--'.
 *
 * @param totalSeconds - The value to format in seconds.
 * @param returnPlaceholder - Whether to return --:--:-- when the time value is undefined or 00:00:00.
 * @returns The formatted time string.
 * @example
 * secondsToHoursMinutesSeconds(3723);
 * // Returns '01:02:03'
 */
export const secondsToDurationString = (
  totalSeconds: number | undefined,
  returnPlaceholder = false
): string => {
  if (totalSeconds === undefined || (totalSeconds === 0 && returnPlaceholder))
    return '--:--:--';

  const time = secondsToHoursMinutesSecondsUnix(totalSeconds);
  return formatHoursMinutesSecondsUnixToString(time);
};

/**
 * Handles the conversion of a date range to an array of normalized values.
 * @param val The date range to normalize.
 * @returns The normalized values.
 * @example
 * getDateRangeNormalizedValues([dayjs('2022-01-01'), dayjs('2022-01-02')]);
 * // Returns [dayjs('2022-01-01'), dayjs('2022-01-02')]
 * @example
 * getDateRangeNormalizedValues([dayjs('2022-01-01'), null]);
 * // Returns [dayjs('2022-01-01'), dayjs('2022-01-01')]
 * @example
 * getDateRangeNormalizedValues([null, dayjs('2022-01-02')]);
 * // Returns [dayjs('2022-01-02'), dayjs('2022-01-02')]
 * @example
 * getDateRangeNormalizedValues([null, null]);
 * // Returns [null, null]
 */
export function getDateRangeNormalizedValues(
  val: DateRange<Dayjs>
): [Dayjs | null, Dayjs | null] {
  const [startValue, endValue] = val;
  if (startValue && endValue) return [startValue, endValue];
  if (!endValue && startValue) return [startValue, startValue];
  if (!startValue && endValue) return [endValue, endValue];
  return [null, null];
}

/**
 * Converts a given date to an ISO string representing either the start or end of the day.
 *
 * @param date - The date to convert. Can be a Dayjs object or null.
 * @param type - Specifies whether to return the start or end of the day.
 * @returns An ISO string representation of the date, or null if the input date is null.
 *
 * @example
 * // Returns the ISO string for the start of the day
 * getDailyISOString(dayjs('2023-04-15'), 'start');
 * // '2023-04-15T00:00:00.000Z'
 *
 * @example
 * // Returns the ISO string for the end of the day
 * getDailyISOString(dayjs('2023-04-15'), 'end');
 * // '2023-04-15T23:59:59.999Z'
 *
 * @example
 * // Returns null for a null input
 * getDailyISOString(null, 'start');
 * // null
 */
export function getDailyISOString(
  date: Dayjs | null,
  type: 'start' | 'end'
): string | null {
  if (!date) return null;
  if (type === 'start') return dayjs(date).startOf('day').toISOString();
  return dayjs(date).endOf('day').toISOString();
}

/*********************************
 *                               *
 * ONLY FOR USE WITHIN THIS FILE *
 *                               *
 *********************************/
export const getPaddedNumber = (number: number): string =>
  number.toString().padStart(2, '0');

export const getClampedNumber = (number: number): number =>
  Math.max(Number(number), 0);

export const getPaddedClampedNumber = (number: number): string =>
  getPaddedNumber(getClampedNumber(number));
